import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getTenantBySlug } from '../services/tenantService';

const TenantContext = createContext(null);

/**
 * TenantProvider — wraps the /t/:tenantSlug route tree.
 *
 * Fetches the tenant by slug from the API, determines the viewer's role
 * within that tenant, and provides:
 *
 *   viewTenant   — full Tenant document (name, slug, branding, moduleConfig, …)
 *   viewerRole   — 'cip_admin' | 'cip_staff' | 'community_admin' | 'community_staff'
 *                  | 'community_member' | null
 *   viewModules  — { [moduleName]: boolean } — which modules are enabled for this tenant
 *   canView      — bool (false only if the API returned 403)
 *   loading      — bool
 *   error        — string | null
 */
export const TenantProvider = ({ children }) => {
  const { tenantSlug } = useParams();
  const { user, memberships, panel } = useAuth();

  const [viewTenant, setViewTenant] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [canView,    setCanView]    = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCanView(true);

    getTenantBySlug(tenantSlug)
      .then((res) => {
        if (!cancelled) setViewTenant(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 403 || status === 404) {
          setCanView(false);
        } else {
          setError(err?.response?.data?.message || 'Failed to load community');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tenantSlug]);

  // Determine the viewer's role within this tenant.
  // Platform staff always get 'cip_staff' (or 'cip_admin').
  const viewerRole = (() => {
    if (!user) return null;
    if (user.globalRole === 'cip_admin') return 'cip_admin';
    if (user.globalRole === 'cip_staff') return 'cip_staff';
    const match = (memberships || []).find((m) => m.tenantSlug === tenantSlug);
    return match?.communityRole || null;
  })();

  // Build enabled-module map from the tenant's moduleConfig.
  // Falls back to all-disabled when tenant hasn't loaded yet.
  const viewModules = viewTenant?.moduleConfig
    ? Object.fromEntries(
        Object.entries(viewTenant.moduleConfig).map(([k, v]) => [k, v?.enabled === true])
      )
    : {};

  const isPlatformStaff = ['admin', 'staff'].includes(panel);

  /**
   * getModuleUrl — resolve the appropriate URL for a module by policy:
   *   If module is disabled → return null
   *   If module is enabled AND externalUrl is set → return externalUrl
   *   If module is enabled AND no externalUrl → return CIP-native route (/t/:slug/:moduleName)
   */
  const getModuleUrl = (moduleName) => {
    const module = viewTenant?.moduleConfig?.[moduleName];
    if (!module || !module.enabled) {
      return null;
    }
    if (module.externalUrl) {
      return module.externalUrl;
    }
    return `/t/${tenantSlug}/${moduleName}`;
  };

  const value = {
    viewTenant,
    viewerRole,
    viewModules,
    canView,
    loading,
    error,
    isPlatformStaff,
    tenantSlug,
    // Convenience helper — platform staff can always access all modules
    isViewModuleEnabled: (name) => isPlatformStaff || !!viewModules[name],
    // Resolve module URL by policy (external or CIP-native)
    getModuleUrl,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside <TenantProvider>');
  return ctx;
};

// Safe version — returns null when called outside TenantProvider (e.g. on platform routes)
export const useTenantSafe = () => useContext(TenantContext);
